import React, {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import {PageHeader} from "../../../../components/PageHeader";
import Search from "../../../../components/search/Search";
import {toKSTISOString, toKSTISOStringEnd} from "../../../../components/commonmethod/CommonMethod";
import {endOfYear, startOfYear} from "date-fns";
import useRequestData from "../../../../hooks/useRequestData";
import {getSlaList} from "../../../../api/evaluation/SlaService";
import {CompareTable} from "./subPages/CompareTable";
import {CompareEditorDialog} from "./subPages/CompareEditorDialog";
import {normalizeSlaMonthKey} from "./slaCompareMonthUtils";
import "./slaCompare.css";
export const calculateEvaluationScoreByTime = (child) => {
    const timeOne = Number(child.operationTimeToInt);
    const timeTwo = Number(child.stopTimeToInt);
    const totalCount = child.totalCount > 0 ? Number(child.totalCount) : 1;
    const totalTime = timeOne * totalCount;

    return ((totalTime - timeTwo) / totalTime * 100).toFixed(2);
};

export const calculateEvaluationScoreUpdateAndCreateByTime = (child) => {
    const timeOne = Number(child.operationTimeToInt);
    const timeTwo = Number(child.stopTimeToInt);

    return ((timeOne - timeTwo) / timeOne * 100).toFixed(2);
};

export const calculateConvertedScoreByTime = (evaluationScore, weight) => {
    return (evaluationScore / (100 / weight)).toFixed(2);
};

export const calculateNumberToDay = (time) => {
    let minute = time % 60;
    let hour = Math.floor(Math.floor(time % 1440) / 60);
    let day = Math.floor(time / 1440);
    return ( day > 0 ? day + "일 " : "") + (hour > 0 ? hour + "시간 " : "") + (minute > 0 ? minute + "분" : "");
}

export const calculateEvaluationScore = (item) => {
    let { countOne, countTwo, weight, section } = item;

    countOne = Number(countOne)
    countTwo = Number(countTwo)
    weight = Number(weight)

    if (countOne === 0 || !countOne){
        switch (section){
            case "ERROR-REPEAT-COUNT" :
            case "OUTPUT" :
            case "IMPROVEMENT" : {
                return 0;
            }
            // 내부자료 유출계산은 아래에서
            case "LEAK" : {
                break;
            }
            default : {
                return 100;
            }
        }
    }

    switch (section) {
        // 장애건수
        case "ERROR-COUNT" : {
            if (countOne <= 1) return (100).toFixed(2);
            if (countOne <= 3) return (80).toFixed(2);
            if (countOne <= 5) return (60).toFixed(2);
            if (countOne <= 7) return (40).toFixed(2);
            if (countOne <= 9) return (20).toFixed(2);
            return (0).toFixed(2);
        }

        // 적기보고
        case "REPORT" : {
            return (countTwo / countOne * 100).toFixed(2);
        }

        // 성능개선, 동일장애 발생건수
        case "IMPROVEMENT" :
        case "ERROR-REPEAT-COUNT" : {
            return countOne * weight;
        }

        // 내부자료 유출
        case "LEAK" : {
            return (countOne + countTwo) * weight;
        }

        // 서비스, 작업, 점검 처리율
        case "SERVICE" :
        case "WORK" :
        case "INSPECTION" : {
            return (countTwo / countOne * 100).toFixed(2);
        }

        // 장애복구율, 산출물
        default : {
            return ((countOne - countTwo) / countOne * 100).toFixed(2);
        }
    }
};

export const calculateConvertedScore = (evaluationScore, item) => {
    const { section, weight } = item;

    if (section === "ERROR-REPEAT-COUNT" || section === "IMPROVEMENT" || section === "LEAK") {
        return evaluationScore;
    }
    return (evaluationScore / (100 / weight)).toFixed(2);
};

function Compare() {

    const navigate = useNavigate();
    const location = useLocation();

    const now = new Date();
    const [date] = useState({
        startDate: toKSTISOString(startOfYear(now)),
        endDate: toKSTISOStringEnd(endOfYear(now)),
    });

    const [editor, setEditor] = useState(null);

    const { data, dataHook, pageInfo} = useRequestData(getSlaList, date, "sla");
    const {handleSearch, handlePageSizeChange, saveConditions, refresh, reload} = dataHook;

    const searchDate = {startDate: saveConditions.startDate ?? date.startDate, endDate: saveConditions.endDate ?? date.endDate};

    useEffect(() => {
        const yearStart = toKSTISOString(startOfYear(new Date()));
        const yearEnd = toKSTISOStringEnd(endOfYear(new Date()));
        const start = saveConditions.startDate ?? date.startDate;
        const end = saveConditions.endDate ?? date.endDate;

        if (start > yearStart || end < yearEnd) {
            handleSearch({startDate: yearStart, endDate: yearEnd});
        }
    }, []);

    useEffect(() => {
        const pending = location.state?.slaEditor;
        if (!pending?.month) {
            return;
        }
        setEditor(pending);
        navigate(location.pathname, {replace: true, state: {}});
    }, [location.pathname, location.state, navigate]);

    const processedData = useMemo(() => {
        if (!Array.isArray(data)) return [];

        return data.map((each) => {
            const next = {
                ...each,
                month: normalizeSlaMonthKey(each.month),
            };

            const sourceChildren = next.children?.length
                ? next.children
                : (next.availability ?? []);

            if (sourceChildren.length > 0) {
                next.children = sourceChildren.map((child) => {
                    const evaluationScore = calculateEvaluationScoreByTime(child);
                    const convertedScore = calculateConvertedScoreByTime(evaluationScore, child.weight);
                    const operationTime = calculateNumberToDay(child.operationTimeToInt);
                    const stopTime = calculateNumberToDay(child.stopTimeToInt);

                    return {
                        ...child,
                        evaluationScore,
                        convertedScore,
                        operationTimeKr: operationTime,
                        stopTimeKr: stopTime,
                    };
                });
            }

            if (next.item?.length > 0) {
                next.item = next.item.map((row) => {
                    const evaluationScore = calculateEvaluationScore(row);
                    const convertedScore = calculateConvertedScore(evaluationScore, row);
                    return {...row, evaluationScore, convertedScore};
                });
            }

            return next;
        });
    }, [data]);

    const handleOpenEditor = (mode, month, monthLabel) => {
        setEditor({mode, month, monthLabel});
    };

    const handleEditorSaved = () => {
        reload();
    };

    return (
        <>
            <div className="common-container container-height-full">
                <PageHeader header="SLA 등록관리" indicator="평가관리/보고서 > SLA 관리 > SLA 등록관리"/>
                <Search refresh={refresh} entity={'sla'} searchConditions={saveConditions} handleSearch={handleSearch} handlePageSizeChange={handlePageSizeChange} date={searchDate} />

                <CompareTable
                    data={processedData}
                    date={saveConditions}
                    onOpenEditor={handleOpenEditor}
                />

                <CompareEditorDialog
                    open={!!editor}
                    mode={editor?.mode}
                    month={editor?.month}
                    monthLabel={editor?.monthLabel}
                    onClose={() => setEditor(null)}
                    onSaved={handleEditorSaved}
                />
            </div>
        </>
    );
}

export default Compare;